import { Lock } from "lucide-react";

import type { Rarity } from "../../api/generated/model";
import type { ThemeFamily } from "../../platform/themes/family";
import { familyTokens } from "../../platform/themes/resolver";
import classes from "./appearance.module.css";

const RARITY: Record<Rarity, string> = {
  free: "Free",
  common: "Common",
  rare: "Rare",
  epic: "Epic",
  legendary: "Legendary",
  mythic: "Mythic",
};

/** One variant of a family as a tiny app: its page, a card with two lines of text and a button. */
function Preview({ family, scheme }: { family: ThemeFamily; scheme: "light" | "dark" }) {
  const tokens = familyTokens(family, scheme);
  return (
    <span className={classes.preview} style={{ background: tokens["--cai-color-page"] }}>
      <span className={classes.previewCard} style={{ background: tokens["--cai-color-surface"] }}>
        <span className={classes.previewLine} style={{ background: tokens["--cai-color-text"] }} />
        <span className={classes.previewShort} style={{ background: tokens["--cai-color-text-muted"] }} />
        <span className={classes.previewButton} style={{ background: tokens["--cai-color-primary"] }} />
      </span>
    </span>
  );
}

/**
 * A family's card: its light and dark variants side by side, its name and rarity. An owned family
 * is chosen by a click; a locked one says where it is won.
 */
export function FamilyCard({
  family,
  rarity,
  owned,
  current,
  onChoose,
}: {
  family: ThemeFamily;
  rarity: Rarity;
  owned: boolean;
  current: boolean;
  onChoose: () => void;
}) {
  const label = owned ? family.name : `${family.name}, locked`;
  return (
    <button
      type="button"
      className={classes.family}
      aria-pressed={current}
      aria-disabled={!owned || undefined}
      aria-label={label}
      data-rarity={rarity}
      onClick={onChoose}
    >
      <span className={classes.previews} aria-hidden>
        <Preview family={family} scheme="light" />
        <Preview family={family} scheme="dark" />
        {owned ? null : (
          <span className={classes.lock}>
            <Lock size={14} />
          </span>
        )}
      </span>
      <span className={classes.familyName}>{family.name}</span>
      <span className={classes.familyMeta}>{current ? "In use" : RARITY[rarity]}</span>
    </button>
  );
}
